import {
  ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, Output, booleanAttribute,
  inject
} from '@angular/core';
import { NgClass } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import { I18NService } from '../../core/i18n/i18n.service';
import { nextAdminuiId } from '../forms/adminui-unique-id';

/** Modal type — drives the leading status icon (mirrors ngx-gaia `GaModalType`). `none` = no icon. */
export type AdminuiDialogType = 'none' | 'info' | 'danger' | 'warning' | 'success';
/** Modal width (mirrors gaia `--small`/`--medium`/`--large`). */
export type AdminuiDialogSize = 'small' | 'medium' | 'large';

/**
 * In-house modal dialog, styled after `@vsn-ux/gaia-styles` `modal.css` using `--ga-*` tokens
 * (reproduced in the component `.scss`, not imported — same approach as adminui-popover/select).
 * Presentational wrapper only: it renders the gaia mask + surface + header/body/footer skeleton
 * once, so each dialog component drops its hand-rolled `.discard-mask`/`.modal` markup. It does
 * NOT own the open/close toggle — the parent still shows it via `@if(showX){ <adminui-x-dialog> }`.
 *
 * WAI-ARIA (modal dialog, per APG): `role="dialog"` (or `alertdialog` for warning/danger
 * confirmations), `aria-modal="true"`, named from the visible title via `aria-labelledby` and
 * described by the projected body via `aria-describedby`. A CDK focus trap (`cdkTrapFocus` +
 * auto-capture) keeps Tab inside the dialog, moves focus to the element marked `cdkFocusInitial`
 * (the primary control, by convention) on open, and restores focus to the trigger on close.
 * Escape and the header close button both emit `(dismiss)` — the host wires that to its existing
 * cancel/close path, so no new behaviour is introduced, only new triggers for the same close.
 * Set `[dismissable]="false"` to suppress Escape + the X.
 */
@Component({
  selector: 'adminui-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, A11yModule],
  templateUrl: './adminui-dialog.component.html',
  styleUrl: './adminui-dialog.component.scss'
})
export class AdminuiDialogComponent {
  /** Visible title; also used as the dialog's accessible name via `aria-labelledby`. */
  @Input({ required: true }) title = '';
  @Input() type: AdminuiDialogType = 'none';
  /** Defaults to `medium` (gaia's default size), which the app uses for its dialogs. */
  @Input() size: AdminuiDialogSize = 'medium';
  /** When true (default) Escape and the header X close the dialog by emitting `(dismiss)`. */
  @Input({ transform: booleanAttribute }) dismissable = true;
  /** User-initiated close (Escape / X). The host wires this to its existing cancel/close. */
  @Output() dismiss = new EventEmitter<void>();

  private readonly uid = nextAdminuiId('adminui-dialog');
  /** Stable id linking the title to the dialog's `aria-labelledby`. */
  readonly titleId = this.uid + '-title';
  /** Stable id linking the projected body to the dialog's `aria-describedby`. */
  readonly bodyId = this.uid + '-body';

  private readonly i18n = inject(I18NService);

  /**
   * WAI-ARIA role: `alertdialog` for destructive confirmations (warning/danger) — which interrupt
   * to require a response — and `dialog` otherwise (APG). `alertdialog` relies on `aria-describedby`
   * pointing at the message, which this component always wires up.
   */
  get dialogRole(): 'dialog' | 'alertdialog' {
    return this.type === 'warning' || this.type === 'danger' ? 'alertdialog' : 'dialog';
  }

  /** Localized accessible name for the header close button (never hardcoded English). */
  get closeLabel(): string {
    return this.i18n.messages['common']?.['ariaLabelClose'] ?? '';
  }

  /** Maps `type` to the app's status icon class; `none` renders no icon. */
  get iconClass(): string | null {
    switch (this.type) {
      case 'danger': return 'vismaicon vismaicon-error';
      case 'warning': return 'vismaicon vismaicon-warning';
      case 'success': return 'vismaicon vismaicon-success';
      case 'info': return 'vismaicon vismaicon-info';
      default: return null;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.dismissable) {
      this.dismiss.emit();
    }
  }

  onCloseClick(): void {
    this.dismiss.emit();
  }
}
